const KEY_ALIKE_SET_ID = 43734373040284;
const KEY_ALIKE_LOCK_MESSAGE = "Please remove the key alike set first";
const LABEL_LOCK_MESSAGE = "Fire label quantity follows the item it was added to";
const disabledRemoveHTML = (message) => `<span class="remove disabled" title="${message}">×</span>`;
const lockedQuantityHTML = ({ quantity, message }) => `<span title="${message}">${quantity}</span>`;

class TrudoorMainCart extends HTMLElement {
  constructor() {
    super();
    this.selectElements();
    this.attachListeners();
  }

  connectedCallback() {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.initializeCart());
    } else {
      this.initializeCart();
    }
  }

  selectElements() {
    const tag = this.querySelector("script#cart-data");
    try {
      this.data = JSON.parse(tag.textContent) ?? {};
      console.log(this.data);
    } catch (error) {
      console.error("Error parsing cart data:", error);
      this.data = {};
    }

    this.section = document.querySelector(`#shopify-section-${this.data.sectionId}`);
    this.removeButtons = document.querySelectorAll("td.product-remove > a.remove");
    this.undo = document.querySelector("#key-alike-undo");
    this.keyAlikeBanner = this.querySelector("cart-key-alike-banner");
    this.keyAlikeSetId = this.data.keyAlikeSetId;
  }

  // A fire label rides along with the frame it was added to: its visible SKU property
  // holds the frame's SKU, and the frame line carries `_labeled` so it stays a line of
  // its own. Cylinder add-ons carry their own SKU, which is how they're told apart.
  isLabelItem(item) {
    return item.properties?._addon === "true" && item.properties.SKU && item.properties.SKU !== item.sku;
  }

  findLabelHost(label) {
    return this.data.cart.items.find(
      (item) => item.sku === label.properties.SKU && item.properties?._labeled === "true",
    );
  }

  // Label quantities are frozen in the UI, so the only way they drift from their host
  // frame is the frame changing (Update Cart post, removal). Reconcile on every render.
  async syncLabelQuantities() {
    const labels = this.data.cart.items.filter((item) => this.isLabelItem(item));
    let changed = false;

    for (const label of labels) {
      const quantity = this.findLabelHost(label)?.quantity ?? 0;
      if (quantity === label.quantity) continue;
      const result = await this.changeCartItem({ id: label.key, quantity });
      // Only re-render on confirmed progress — a failed or capped change would
      // otherwise re-render into the same mismatch and loop against /cart/change.js
      const newQuantity = result?.items ? result.items.find((i) => i.key === label.key)?.quantity ?? 0 : label.quantity;
      if (newQuantity !== label.quantity) changed = true;
    }

    if (changed) {
      const html = await SectionRenderingApi.fetchSection({ id: this.data.sectionId });
      if (html) this.section.innerHTML = html.innerHTML;
    }

    return changed;
  }

  async initializeCart() {
    try {
      if (!this.data?.cart?.items) return;

      // Re-rendering the section rebuilds this element, which re-runs with fresh data
      if (await this.syncLabelQuantities()) return;

      this.data.cart.items
        .filter((item) => this.isLabelItem(item))
        .forEach((label) => {
          this.freezeCartItem(label.key, { editable: false, removable: false, message: LABEL_LOCK_MESSAGE });
        });

      const keysets = this.data.cart.items.filter((item) => item.variant_id === this.keyAlikeSetId);
      keysets.forEach((set) => {
        this.freezeCartItem(set.key, { removable: true });
        if (set.properties && Array.isArray(set.properties["_SKUs"])) {
          set.properties["_SKUs"].forEach((sku) => {
            // Find the matching cart item by SKU
            const matchingItems = this.data.cart.items.filter((item) => item.sku === sku);
            matchingItems.forEach((item) => {
              this.freezeCartItem(item.key, { editable: false, removable: false });
            });
          });
        }
      });
    } catch (err) {
      console.error("Error initializing cart:", err);
    } finally {
      LoadingManager.hideAll();
    }
  }

  async freezeCartItem(id, { removable = false, editable = false, message = KEY_ALIKE_LOCK_MESSAGE } = {}) {
    try {
      const row = this.querySelector(`tr[data-id="${id}"]`) ?? this.querySelector(`tr[data-key="${id}"]`);
      if (!row) return;

      if (!removable) {
        // Replace the remove button with a disabled version
        const removeCell = row.querySelector("td.product-remove");
        if (removeCell) {
          // Store original HTML for debugging
          const originalHTML = removeCell.innerHTML;

          // First, remove any existing click event listeners by cloning the element
          const newRemoveCell = removeCell.cloneNode(true);
          removeCell.parentNode.replaceChild(newRemoveCell, removeCell);

          // Now replace with disabled version
          newRemoveCell.innerHTML = disabledRemoveHTML(message);

          // Add a click handler that prevents default and stops propagation
          const disabledBtn = newRemoveCell.querySelector(".remove.disabled");
          if (disabledBtn) {
            disabledBtn.addEventListener("click", (e) => {
              e.preventDefault();
              e.stopPropagation();
              alert(message);
              return false;
            });
          }
        }
      }

      if (!editable) {
        const quantity = row.querySelector("td.product-quantity");
        const value = quantity.querySelector("input.qty")?.value;
        if (value) {
          // Clone to remove event listeners
          const newQuantity = quantity.cloneNode(false);
          quantity.parentNode.replaceChild(newQuantity, quantity);
          newQuantity.innerHTML = lockedQuantityHTML({ quantity: value, message });
        }
      }
    } catch (err) {
      console.error("Error freezing cart item:", err);
    }
  }

  async addToCart({ items = [] }) {
    LoadingManager.show({ elementId: "cart-form" });
    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        body: JSON.stringify({
          items,
          sections: this.data.sectionId,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      });
      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.description || "Error adding to cart");
      }

      if (json.sections?.[this.data.sectionId]) {
        this.refreshCart(json.sections[this.data.sectionId]);
      }
      return json;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      LoadingManager.hide({ elementId: "cart-form" });
    }
  }

  async changeCartItem(item) {
    try {
      if (!item?.id) {
        console.error("Invalid item ID for cart change:", item);
        return null;
      }

      const res = await fetch("/cart/change.js", {
        method: "POST",
        body: JSON.stringify({
          ...item,
          sections: this.data.sectionId,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      });
      const json = await res.json();
      return json;
    } catch (err) {
      console.error(err);
    }
  }

  async removeCartItem(key) {
    LoadingManager.show({ sectionId: this.data.sectionId });
    try {
      const res = await fetch("/cart/change.js", {
        method: "POST",
        body: JSON.stringify({
          id: key,
          quantity: 0,
          // Bundled section rendering doesn't work here fsr, returns bad json
        }),
        headers: {
          "Content-Type": "application/json",
        },
      });
      const json = await res.json();

      const html = await SectionRenderingApi.fetchSection({ id: this.data.sectionId });
      if (html) this.section.innerHTML = html.innerHTML;

      return json;
    } catch (err) {
      console.error("Cart remove error:", err);
    } finally {
      LoadingManager.hide({ sectionId: this.data.sectionId });
    }
  }

  async refreshCart(html) {
    if (!html || !this.section) return;

    const content = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(`#shopify-section-${this.data.sectionId}`)?.innerHTML;
    if (content) {
      this.section.innerHTML = content;

      // Re-initialize after refresh to freeze items again
      setTimeout(() => this.initializeCart(), 100);
    }
    document.dispatchEvent(new CustomEvent("cart:refreshed"));
  }

  renderUndo() {
    try {
      const removed = Store.cart.lastRemovedItem;
      this.lastRemovedItem = removed;

      if (removed?.variant_id === this.keyAlikeSetId) {
        Store.cart.lastRemovedItem = null;
        return;
      }

      if (this.data.cart.items.some((item) => item.key === removed?.key)) {
        return (Store.cart.lastRemovedItem = null);
      }

      if (removed) {
        this.showUndo(removed);
      } else {
        this.undo.style.display = "none";
      }
    } catch (err) {
      console.log(err);
    }
  }

  showUndo(item) {
    this.undo.style.display = "block";
    this.undo.querySelector("#item").innerText = item.product_title;
    this.undo.querySelector("a").addEventListener("click", async (e) => {
      e.preventDefault();
      try {
        LoadingManager.show({ sectionId: this.data.sectionId });
        await this.addToCart({
          items: [
            {
              id: item.variant_id,
              quantity: item.quantity,
              properties: item.properties,
            },
          ],
        });
      } finally {
        LoadingManager.hide({ sectionId: this.data.sectionId });
      }
    });
  }

  attachListeners() {
    this.removeButtons.forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        const removed = this.data.cart.items.find((item) => item.key === e.currentTarget.dataset.key);
        Store.cart.lastRemovedItem = removed;
        this.removeCartItem(e.currentTarget.dataset.key);
      });
    });

    $("input.qty").change(() => {
      $('input[name="update_cart"]').removeAttr("disabled");
    });

    // Add a global event handler to prevent removal of locked items
    document.addEventListener(
      "click",
      (e) => {
        const disabled = e.target?.closest?.(".remove.disabled");
        if (disabled) {
          e.preventDefault();
          e.stopPropagation();
          alert(disabled.title || KEY_ALIKE_LOCK_MESSAGE);
          return false;
        }
      },
      true,
    );
  }
}

customElements.define("trudoor-main-cart", TrudoorMainCart);
